import { Button, Group, Select, Stack, TextInput } from '@mantine/core'
import { isNotEmpty, useForm } from '@mantine/form'
import { FormattedMessage, useIntl } from 'react-intl'
import { Device } from '@/types/device'
import { isValidStreamInput } from '../../../utils/stream_input'
import { RtspExamples } from './RtspExamples'

export type FormValues = {
  cameraName: string
  deviceId: string
  rtspUrl: string
}

type AddCameraFormProps = {
  devices: Device[]
  onCancel: () => void
  onSubmit: (formValues: FormValues) => void
}

export const AddCameraForm = ({
  devices,
  onCancel,
  onSubmit
}: AddCameraFormProps) => {
  const intl = useIntl()

  const form = useForm({
    initialValues: {
      cameraName: '',
      deviceId: devices[0]?.id || '',
      rtspUrl: ''
    },

    validate: {
      cameraName: isNotEmpty(
        intl.formatMessage({ id: 'cameras.add.form.name.required' })
      ),
      deviceId: isNotEmpty(
        intl.formatMessage({ id: 'cameras.add.form.device.required' })
      ),
      rtspUrl: (value) => {
        if (!isValidStreamInput(value)) {
          return intl.formatMessage({
            id: 'cameras.add.form.rtspUrl.invalid'
          })
        }

        return null
      }
    }
  })

  return (
    <form noValidate onSubmit={form.onSubmit((values) => onSubmit(values))}>
      <Stack gap="lg">
        <TextInput
          label={<FormattedMessage id="cameras.fields.name" />}
          autoFocus
          data-autofocus // required for auto focus inside the modal
          {...form.getInputProps('cameraName')}
        />

        <Select
          label={<FormattedMessage id="cameras.fields.device" />}
          data={devices.map((device) => ({
            value: device.id,
            label: device.label
          }))}
          allowDeselect={false}
          nothingFoundMessage={
            <FormattedMessage id="cameras.device.noOnlineDevices" />
          }
          {...form.getInputProps('deviceId')}
        />

        <TextInput
          label={<FormattedMessage id="cameras.add.form.rtspUrl" />}
          {...form.getInputProps('rtspUrl')}
        />

        <RtspExamples />

        <Group justify="end">
          <Button miw={120} variant="default" onClick={onCancel}>
            <FormattedMessage id="cancel" />
          </Button>

          <Button miw={120} type="submit">
            <FormattedMessage id="cameras.add" />
          </Button>
        </Group>
      </Stack>
    </form>
  )
}
